import { Link } from "react-router-dom";
import { Linkedin, Mail, MapPin, Phone, Rss } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSettings } from "@/lib/useSettings";

const SubstackIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.539 0H1.46v2.836h21.08V0z" />
  </svg>
);

const Footer = () => {
  const { t, lang } = useLang();
  const { settings } = useSettings();
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const addr = [settings.address_street, settings.address_postal_code, settings.address_city, settings.address_country]
    .filter(Boolean)
    .join(", ");

  return (
    <footer data-testid="site-footer" className="mt-20 border-t border-[#E5E7EB] bg-[#111827] text-[#F7F7F2]">
      <div className="dl-container py-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878]">Dataleo</div>
          <h3 className="font-display text-3xl mt-3 leading-tight">
            AI news for Operations leaders.
            <br />
            <span className="text-[#00A878]">Less hype. More decision impact.</span>
          </h3>
          <p className="mt-4 text-sm text-[#9ca3af] max-w-md">{t.positioning.body}</p>

          {/* Business identity — drives LocalBusiness + ContactPoint schema */}
          <address className="mt-5 text-[12px] not-italic text-[#9ca3af] space-y-1.5">
            {addr && (
              <div className="flex items-start gap-2" data-testid="footer-address">
                <MapPin size={13} className="mt-0.5 text-[#00A878] shrink-0" />
                <span>{addr}</span>
              </div>
            )}
            {settings.phone && (
              <div className="flex items-center gap-2" data-testid="footer-phone">
                <Phone size={13} className="text-[#00A878]" />
                <a href={`tel:${settings.phone.replace(/\s+/g, "")}`} className="hover:text-white">
                  {settings.phone}
                </a>
              </div>
            )}
            {settings.contact_email && (
              <div className="flex items-center gap-2">
                <Mail size={13} className="text-[#00A878]" />
                <a href={`mailto:${settings.contact_email}`} className="hover:text-white">
                  {settings.contact_email}
                </a>
              </div>
            )}
          </address>

          {/* Social profiles — official LinkedIn always rendered; others
              only when explicitly configured (no fake placeholder links). */}
          {(() => {
            const linkedinDataleo = settings.linkedin_dataleo_url || "https://www.linkedin.com/company/dataleo/";
            return (
              <div className="mt-5 flex items-center gap-3" data-testid="footer-social">
                <a
                  href={linkedinDataleo}
                  target="_blank"
                  rel="noopener noreferrer me"
                  data-testid="social-linkedin-dataleo"
                  aria-label="LinkedIn Dataleo"
                  className="w-9 h-9 grid place-items-center border border-[#1F2937] hover:border-[#00A878] hover:text-[#00A878] transition-colors"
                >
                  <Linkedin size={15} />
                </a>
                {settings.linkedin_jfn_url && (
                  <a
                    href={settings.linkedin_jfn_url}
                    target="_blank"
                    rel="noopener noreferrer me"
                    data-testid="social-linkedin-jfn"
                    aria-label="LinkedIn Jean-François Nordmann"
                    className="w-9 h-9 grid place-items-center border border-[#1F2937] hover:border-[#00A878] hover:text-[#00A878] transition-colors text-[10px] font-mono-dl"
                  >
                    JFN
                  </a>
                )}
                {settings.substack_url && (
                  <a
                    href={settings.substack_url}
                    target="_blank"
                    rel="noopener noreferrer me"
                  data-testid="social-substack"
                  aria-label="Substack Dataleo"
                  className="w-9 h-9 grid place-items-center border border-[#1F2937] hover:border-[#00A878] hover:text-[#00A878] transition-colors"
                >
                  <SubstackIcon size={14} />
                </a>
              )}
              </div>
            );
          })()}
        </div>

        <div>
          <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#9ca3af]">{t.nav.news}</div>
          <ul className="mt-3 space-y-2 text-sm">
            <li><Link to="/news" className="hover:text-[#00A878]">{t.nav.news}</Link></li>
            <li><Link to="/jobs" className="hover:text-[#00A878]">{t.nav.jobs}</Link></li>
            <li><Link to="/alerts" className="hover:text-[#00A878]">{t.nav.alerts}</Link></li>
            <li><Link to="/insights" className="hover:text-[#00A878]">{t.nav.insights}</Link></li>
            <li><Link to="/tutorials" className="hover:text-[#00A878]">{t.nav.tutorials}</Link></li>
            <li><Link to="/ecosystem" data-testid="footer-ecosystem-link" className="hover:text-[#00A878]">{t.nav.ecosystem}</Link></li>
          </ul>
        </div>

        <div>
          <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#9ca3af]">Dataleo</div>
          <ul className="mt-3 space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-[#00A878]">{t.nav.about}</Link></li>
            <li>
              <Link to="/submit" data-testid="footer-submit-link" className="hover:text-[#00A878] inline-flex items-center gap-1">
                <span className="text-[#FFD400]">+</span> {lang === "fr" ? "Soumettre une news" : "Submit a news"}
              </Link>
            </li>
            <li><Link to="/contact" data-testid="footer-contact-link" className="hover:text-[#00A878]">Contact</Link></li>
          </ul>
          <div className="mt-4 pt-3 border-t border-[#1F2937] space-y-1 text-[11px] font-mono-dl">
            <a href={`${apiBase}/api/rss.xml`} target="_blank" rel="noreferrer" className="block text-[#9ca3af] hover:text-[#00A878] inline-flex items-center gap-1">
              <Rss size={11} /> RSS feed
            </a>
            <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="block text-[#9ca3af] hover:text-[#00A878]">sitemap.xml</a>
          </div>
        </div>
      </div>

      <div className="border-t border-[#1F2937]" data-nosnippet>
        <div className="dl-container py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-[#9ca3af]">
          <div>© {new Date().getFullYear()} Dataleo. {t.footer.rights}</div>
          <div className="font-mono-dl">{t.footer.built}</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
